import * as React from "react";
import { Stack, styled, SxProps } from "@mui/material";
import PlayerMarker, { PlayerMarkerClip } from "./PlayerMarker";
import { useSelector } from "react-redux";
import { GetFrameRate, GetTotalFrameCount } from "../../../Redux/Reducers/redux-slice-react-player";
import { ClipCardProps } from "../../Cards/ClipCard/clip-card";
import { QCMarkerCardProps } from "../../Cards/QCMarkerCard/qc-marker-card";

export interface TimeCode {
  originalObject: any;
}

interface MarkerType extends TimeCode {
  left: number;
  right: number;
  width: number;
  focused?: boolean;
}

export type MediaPlayerMarkersProps = {
  sx?: SxProps;
  timecodes: QCMarkerCardProps[];
};

export type MediaPlayerClipsProps = {
  sx?: SxProps;
  clips: ClipCardProps[];
  focusedClip: ClipCardProps | null;
};

const MediaPlayerMarkers: React.FC<MediaPlayerMarkersProps> = (props) => {
  const frameRate = useSelector(GetFrameRate);
  const totalFrameCount = useSelector(GetTotalFrameCount);
  const { timecodes } = props;

  const rowTimecodes = React.useMemo(() => {
    const _rowTimecodes: MarkerType[][] = [];
    timecodes.forEach((marker) => {
      let leftPos = (marker.timecodes.in.markFrame * 100) / totalFrameCount;
      const width = Math.max(
        ((marker.timecodes.out.markFrame - marker.timecodes.in.markFrame) * 100) / totalFrameCount,
        2
      );
      let rightPos = leftPos + width;
      if (rightPos > 100) {
        leftPos -= rightPos - 100;
        rightPos = 100;
      }

      let targetRowIndex = 0;

      for (let k = 0; k < _rowTimecodes.length; k++) {
        let canBeOnThisRow = true;
        const row = _rowTimecodes[k];

        for (let i = 0; i < row.length; i++) {
          const timecode = row[i];
          const startDuringEndDuring = leftPos >= timecode.left && rightPos <= timecode.right;
          const startBeforeEndAfter = leftPos <= timecode.left && rightPos >= timecode.right;
          const startDuringEndAfter =
            leftPos >= timecode.left && leftPos <= timecode.right && rightPos >= timecode.right;
          const startBeforeEndDuring =
            leftPos < timecode.left && rightPos >= timecode.left && rightPos <= timecode.right;
          const isInRange = startDuringEndDuring || startBeforeEndAfter || startDuringEndAfter || startBeforeEndDuring;

          if (isInRange) {
            canBeOnThisRow = false;
            targetRowIndex = k + 1;
            break;
          }
        }

        if (canBeOnThisRow) break;
      }

      const newTimecode = {
        originalObject: marker,
        width,
        left: leftPos,
        right: rightPos,
      };

      if (!_rowTimecodes[targetRowIndex]) {
        _rowTimecodes[targetRowIndex] = [];
      }
      _rowTimecodes[targetRowIndex].push(newTimecode);
    });
    return _rowTimecodes;
  }, [timecodes, totalFrameCount]);

  return (
    <StyledRail flexDirection="row" {...props}>
      <Stack>
        {rowTimecodes.map((row, i) => (
          <StyledRow key={i}>
            {row.map((timecode, timecodeId) => (
              <PlayerMarker
                key={timecodeId}
                originalObject={timecode.originalObject}
                left={timecode.left}
                width={timecode.width}
              />
            ))}
          </StyledRow>
        ))}
      </Stack>
    </StyledRail>
  );
};

export const MediaPlayerClips: React.FC<MediaPlayerClipsProps> = (props) => {
  const totalFrameCount = useSelector(GetTotalFrameCount);
  const { clips } = props;

  const rowTimecodes = React.useMemo(() => {
    const _rowTimecodes: MarkerType[][] = [];
    clips.forEach((clip) => {
      let leftPos = (clip.timecodes.in.markFrame * 100) / totalFrameCount;
      const width = Math.max(((clip.timecodes.out.markFrame - clip.timecodes.in.markFrame) * 100) / totalFrameCount, 2);
      let rightPos = leftPos + width;
      if (rightPos > 100) {
        leftPos -= rightPos - 100;
        rightPos = 100;
      }

      let targetRowIndex = 0;

      for (let k = 0; k < _rowTimecodes.length; k++) {
        let canBeOnThisRow = true;
        const row = _rowTimecodes[k];

        for (let i = 0; i < row.length; i++) {
          const timecode = row[i];
          const startDuringEndDuring = leftPos >= timecode.left && rightPos <= timecode.right;
          const startBeforeEndAfter = leftPos <= timecode.left && rightPos >= timecode.right;
          const startDuringEndAfter =
            leftPos >= timecode.left && leftPos <= timecode.right && rightPos >= timecode.right;
          const startBeforeEndDuring =
            leftPos < timecode.left && rightPos >= timecode.left && rightPos <= timecode.right;
          const isInRange = startDuringEndDuring || startBeforeEndAfter || startDuringEndAfter || startBeforeEndDuring;

          if (isInRange) {
            canBeOnThisRow = false;
            targetRowIndex = k + 1;
            break;
          }
        }

        if (canBeOnThisRow) break;
      }

      const newTimecode = {
        originalObject: clip,
        width,
        left: leftPos,
        right: rightPos,
        focused: clip.id === props.focusedClip?.id,
      };

      if (!_rowTimecodes[targetRowIndex]) {
        _rowTimecodes[targetRowIndex] = [];
      }
      _rowTimecodes[targetRowIndex].push(newTimecode);
    });
    return _rowTimecodes;
  }, [clips, totalFrameCount]);

  return (
    <StyledRail flexDirection="row" {...props}>
      {rowTimecodes.map((row, i) => (
        <StyledRow key={i}>
          {row.map((timecode, timecodeId) => (
            <PlayerMarkerClip
              key={timecodeId}
              originalObject={timecode.originalObject}
              left={timecode.left}
              width={timecode.width}
            />
          ))}
        </StyledRow>
      ))}
    </StyledRail>
  );
};

export default MediaPlayerMarkers;

const StyledRail = styled(Stack)(({ theme }) => ({
  position: "relative",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  overflow: "scroll",
  scrollbarWidth: "none",
  display: "flex",
  flexDirection: "column",
  // 4 markers + padding
  maxHeight: "124px",
  transition: "max-height 0.3s",
}));

const StyledRow = styled(Stack)(({ theme }) => ({
  position: "relative",
  flexShrink: 0,
  height: 16,
  "& + div": {
    marginTop: 2,
  },
}));
